import React from "react";
import { useParams } from "react-router";
import { gql, useQuery, useMutation } from "@apollo/client";
import { Form } from "react-final-form";
import { TextField } from "@syos-music/form";

import {
  Grid,
  Fab,
  Tooltip,
  TextField as MuiTextField,
  Container,
  Box,
} from "@mui/material";

import useLocalStorage from "lib/localStorage";
import Layout from "components/common/Layout";
import SaveFab from "components/common/SaveFab";
import MarkdownField from "components/form/MarkdownField";
import MailPreview from "components/common/MailPreview";

const FrIcon = (props) => (
  <Box component="span" role="img" aria-label="Français" {...props}>
    🇫🇷
  </Box>
);
const EnIcon = (props) => (
  <Box component="span" role="img" aria-label="Anglais" {...props}>
    🇬🇧
  </Box>
);

const QUERY = gql`
  query ($id: ID!) {
    template(id: $id) {
      id
      name
      subject {
        en
        fr
      }
      content {
        en
        fr
      }
    }

    user {
      id

      ...MailPreview_user
    }
  }

  ${MailPreview.fragments.user}
`;

const ORDER_QUERY = gql`
  query ($orderId: ID!) {
    order(id: $orderId) {
      id

      ...MailPreview_order
    }
  }

  ${MailPreview.fragments.order}
`;

const MUTATION = gql`
  mutation (
    $id: ID!
    $name: String!
    $subject: IntlStringInput
    $content: IntlStringInput
  ) {
    UpdateTemplate(id: $id, name: $name, subject: $subject, content: $content) {
      template {
        id
        name
        subject {
          en
          fr
        }
        content {
          en
          fr
        }
      }
    }
  }
`;

export default function TemplatePage() {
  const { templateId } = useParams();
  const [locale, setLocale] = React.useState("fr");

  const [previewOrderId, setPreviewOrderId] = useLocalStorage(
    "lapidou::mails::template::previewOrderId"
  );

  const { data, loading } = useQuery(QUERY, {
    variables: { id: templateId },
  });

  const orderQuery = useQuery(ORDER_QUERY, {
    variables: { orderId: previewOrderId },
    skip: !previewOrderId,
  });

  const [updateTemplate] = useMutation(MUTATION);

  if (loading) return <Layout.Loading />;
  if (!data?.template) return <Layout.NotFound />;

  return (
    <Container fixed sx={{ p: 2 }}>
      <Form
        onSubmit={(values) =>
          updateTemplate({
            variables: {
              id: data.template.id,
              ...values,
            },
          })
            .then(() => {})
            .catch(() => true)
        }
        initialValues={{
          name: data.template.name,
          subject: data.template.subject ?? { en: "", fr: "" },
          content: data.template.content ?? { en: "", fr: "" },
        }}
        keepDirtyOnReinitialize
      >
        {({ handleSubmit, pristine, submitting, form, values }) => (
          <form onSubmit={handleSubmit} noValidate autoComplete="off">
            <Layout.Toolbar fixed>
              <Grid container alignItems="center">
                <Grid item xs>
                  <TextField name="name" required label="Nom" />
                </Grid>

                <Grid item>
                  <Tooltip title="Passer à la version française du template">
                    <div>
                      <Fab
                        onClick={() => setLocale("fr")}
                        disabled={locale === "fr"}
                        size="small"
                      >
                        <FrIcon />
                      </Fab>
                    </div>
                  </Tooltip>
                </Grid>

                <Grid item>
                  <Tooltip title="Passer à la version anglaise du template">
                    <div>
                      <Fab
                        onClick={() => setLocale("en")}
                        disabled={locale === "en"}
                        size="small"
                      >
                        <EnIcon />
                      </Fab>
                    </div>
                  </Tooltip>
                </Grid>

                <Grid item>
                  <SaveFab
                    onSave={handleSubmit}
                    disabled={pristine}
                    loading={submitting}
                  />
                </Grid>
              </Grid>
            </Layout.Toolbar>

            <Grid container>
              <Grid item xs={6}>
                <TextField
                  name={`subject.${locale}`}
                  label={
                    <>
                      {locale === "fr" && <FrIcon mr="0.5ch" />}
                      {locale === "en" && <EnIcon mr="0.5ch" />}
                      Sujet
                    </>
                  }
                  required
                />
              </Grid>

              <Grid item xs={6}>
                <MuiTextField
                  value={previewOrderId ?? ""}
                  onChange={(evt) => setPreviewOrderId(evt.target.value)}
                  label="Prévisualisation"
                  helperText="Identifiant de la commande à prévisualiser"
                />
              </Grid>

              <Grid item xs={6}>
                <MarkdownField name={`content.${locale}`} />
              </Grid>

              <Grid item xs={6}>
                {orderQuery.data?.order && (
                  <MailPreview
                    user={data.user}
                    order={orderQuery.data.order}
                    template={values}
                    locale={locale}
                  />
                )}
              </Grid>
            </Grid>
          </form>
        )}
      </Form>
    </Container>
  );
}
